import { useState, useEffect } from 'react';
import { FileCheck, CheckCircle2, XCircle, AlertCircle } from 'lucide-react';
import { ListaVerificacaoAdministrativa as ListaVerificacaoType, ItemVerificacao, ITENS_VERIFICACAO_PADRAO } from '@/types/lista-verificacao';
import { useI18n } from '@/contexts/i18n-hook';
import { descricaoItemVerificacao } from './constants/lista-verificacao-i18n';

interface ListaVerificacaoAdministrativaProps {
  dados?: ListaVerificacaoType | null;
  onChange?: (dados: ListaVerificacaoType) => void;
  modoEdicao?: boolean;
}

export function ListaVerificacaoAdministrativa({
  dados,
  onChange,
  modoEdicao = false,
}: ListaVerificacaoAdministrativaProps) {
  const { t } = useI18n();

  // Converter dados de objeto para array se necessário
  const converterDadosParaArray = (dados?: ListaVerificacaoType | null): ItemVerificacao[] => {
    if (!dados) {
      return ITENS_VERIFICACAO_PADRAO.map(item => ({ ...item }));
    }
    
    // Se for array (formato antigo)
    if (Array.isArray(dados) || (dados as any).itens) {
      const itensArray = Array.isArray(dados) ? dados : (dados as any).itens;
      if (itensArray && itensArray.length > 0) {
        return itensArray;
      }
    }
    
    // Se for objeto com chaves C.4.1, C.4.2, etc.
    if (typeof dados === 'object' && !Array.isArray(dados)) {
      // Após a remoção do antigo C.4.1 e renumeração, dados salvos por chave
      // numérica não casam mais com as descrições atuais. Para preservar as
      // respostas, buscamos primeiro pela descrição (estável) e só caímos para o
      // código atual quando a busca por descrição não encontrar nada.
      const dadosObj = dados as any;
      const valoresSalvos = Object.values(dadosObj) as any[];
      const itensArray: ItemVerificacao[] = [];
      ITENS_VERIFICACAO_PADRAO.forEach(itemPadrao => {
        const porDescricao = valoresSalvos.find(
          (v) => v && typeof v.descricao === 'string' && v.descricao === itemPadrao.descricao
        );
        const itemDados = porDescricao || dadosObj[itemPadrao.codigo];
        if (itemDados) {
          itensArray.push({
            codigo: itemPadrao.codigo,
            descricao: itemPadrao.descricao,
            possui: itemDados.possui !== undefined ? itemDados.possui : null,
            observacao: itemDados.observacao || '',
          });
        } else {
          itensArray.push({ ...itemPadrao });
        }
      });
      return itensArray;
    }
    
    return ITENS_VERIFICACAO_PADRAO.map(item => ({ ...item }));
  };

  const [itens, setItens] = useState<ItemVerificacao[]>(() => {
    return converterDadosParaArray(dados);
  });

  useEffect(() => {
    setItens(converterDadosParaArray(dados));
  }, [dados]);

  const atualizarItem = (index: number, campo: keyof ItemVerificacao, valor: any) => {
    const novosItens = [...itens];
    novosItens[index] = {
      ...novosItens[index],
      [campo]: valor,
    };
    setItens(novosItens);
    
    // Converter array para objeto com chaves C.4.1, C.4.2, etc.
    const listaObjeto: any = {};
    novosItens.forEach(item => {
      listaObjeto[item.codigo] = {
        item: item.codigo,
        descricao: item.descricao,
        possui: item.possui,
        observacao: item.observacao || '',
      };
    });
    onChange?.(listaObjeto);
  };

  const getPossuiIcon = (possui: boolean | null) => {
    if (possui === true) {
      return <span className="inline-flex p-0.5 rounded ring-1 ring-green-400"><CheckCircle2 className="h-5 w-5 text-green-500" /></span>;
    }
    if (possui === false) {
      return <span className="inline-flex p-0.5 rounded ring-1 ring-red-400"><XCircle className="h-5 w-5 text-red-500" /></span>;
    }
    return <span className="inline-flex p-0.5 rounded ring-1 ring-amber-400"><AlertCircle className="h-5 w-5 text-amber-500" /></span>;
  };

  const getPossuiLabel = (possui: boolean | null) => {
    if (possui === true) return t('comum.rotulo.sim');
    if (possui === false) return t('comum.rotulo.nao');
    return t('apreciacaoTabelas.administrativa.possui.naoInformado');
  };

  return (
    <div className="bg-white dark:bg-schmersal-gray-darker shadow rounded-lg p-6 space-y-4 border border-rg-line dark:border-schmersal-gray-dark">
      <div className="flex items-center mb-4">
        <FileCheck className="h-6 w-6 text-blue-500 dark:text-schmersal-blue mr-2" />
        <h2 className="text-xl font-bold text-gray-900 dark:text-white">
          {t('apreciacaoTabelas.administrativa.titulo')}
        </h2>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-schmersal-gray-dark">
          <thead className="bg-gray-50 dark:bg-schmersal-gray-dark">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 dark:text-white uppercase tracking-wider">
                {t('apreciacaoTabelas.administrativa.coluna.item')}
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 dark:text-white uppercase tracking-wider">
                {t('comum.rotulo.descricao')}
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-700 dark:text-white uppercase tracking-wider">
                {t('apreciacaoTabelas.administrativa.coluna.possui')}
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 dark:text-white uppercase tracking-wider">
                {t('apreciacaoTabelas.administrativa.coluna.observacao')}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-schmersal-gray-darker divide-y divide-gray-200 dark:divide-schmersal-gray-dark">
            {itens.map((item, index) => (
              <tr key={item.codigo} className="hover:bg-gray-50 dark:hover:bg-schmersal-gray-dark">
                <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                  {item.codigo}
                </td>
                <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                  {descricaoItemVerificacao(t, item.codigo, item.descricao)}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-center">
                  {modoEdicao ? (
                    <div className="flex items-center justify-center gap-2">
                      <button
                        type="button"
                        onClick={() => atualizarItem(index, 'possui', true)}
                        className={`p-1 rounded ${
                          item.possui === true
                            ? 'bg-green-100 dark:bg-green-900 ring-1 ring-green-400'
                            : 'bg-gray-100 dark:bg-schmersal-gray-dark hover:bg-green-50 dark:hover:bg-green-900'
                        }`}
                        title={t('apreciacaoTabelas.administrativa.acao.possui')}
                      >
                        <CheckCircle2 className="h-5 w-5 text-green-500" />
                      </button>
                      <button
                        type="button"
                        onClick={() => atualizarItem(index, 'possui', false)}
                        className={`p-1 rounded ${
                          item.possui === false
                            ? 'bg-red-100 dark:bg-red-900 ring-1 ring-red-400'
                            : 'bg-gray-100 dark:bg-schmersal-gray-dark hover:bg-red-50 dark:hover:bg-red-900'
                        }`}
                        title={t('apreciacaoTabelas.administrativa.acao.naoPossui')}
                      >
                        <XCircle className="h-5 w-5 text-red-500" />
                      </button>
                      <button
                        type="button"
                        onClick={() => atualizarItem(index, 'possui', null)}
                        className={`p-1 rounded ${
                          item.possui === null
                            ? 'bg-amber-100 dark:bg-amber-900 ring-1 ring-amber-400'
                            : 'bg-gray-50 dark:bg-schmersal-gray-darkest hover:bg-amber-50 dark:hover:bg-amber-950'
                        }`}
                        title={t('apreciacaoTabelas.administrativa.acao.naoInformado')}
                      >
                        <AlertCircle className={`h-5 w-5 ${item.possui === null ? 'text-amber-500' : 'text-gray-400'}`} />
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center">
                      {getPossuiIcon(item.possui)}
                      <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                        {getPossuiLabel(item.possui)}
                      </span>
                    </div>
                  )}
                </td>
                <td className="px-4 py-3">
                  {modoEdicao ? (
                    <input
                      type="text"
                      value={item.observacao || ''}
                      onChange={(e) => atualizarItem(index, 'observacao', e.target.value)}
                      className="w-full border-gray-300 dark:border-schmersal-gray-dark rounded-md shadow-sm 
                               focus:ring-blue-500 dark:focus:ring-schmersal-blue focus:border-blue-500 dark:focus:border-schmersal-blue 
                               bg-white dark:bg-schmersal-gray-darkest text-gray-900 dark:text-white
                               sm:text-sm"
                      placeholder={t('apreciacaoTabelas.administrativa.placeholderObservacao')}
                    />
                  ) : (
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      {item.observacao || '-'}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

